import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FiCalendar, FiMail, FiUsers, FiClock, FiAlertCircle, FiFilter, FiArchive, FiTrash2, FiFolder, FiCornerUpLeft, FiUser, FiUserPlus, FiCheck } from "react-icons/fi";
import Select from "react-select";
import Card from "../utils/Card";
import GoogleCalendarView from "./GoogleCalendarView";
import PeopleDetails from "../utils/PeopleDetails";
import InteractionContextMenu from "../utils/InteractionContextMenu";
import ContextMenuPortal from "../utils/ContextMenuPortal";
import EmailReplyModal from "../utils/EmailReplyModal";
import EmailMoveModal from "../utils/EmailMoveModal";
import AddPersonFromEmailModal from "../utils/AddPersonFromEmailModal";
import { peopleColumns } from "../people/People";
import api from "../../api";
import "./ToDo.css";

const ToDo = ({ showToast, currentUser, preloadedData, onTaskUpdate }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState({
    meetings: [],
    allSentEmails: [],
    allReceivedEmails: [],
    unrespondedSentEmails: [],
    unrespondedReceivedEmails: [],
    draftEmails: [],
    followUps: []
  });
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  // Debug: Track selectedPerson changes
  useEffect(() => {
    console.log('[ToDo] selectedPerson state changed to:', selectedPerson ? { id: selectedPerson.id, name: selectedPerson.name } : null);
  }, [selectedPerson]);
  const [interactionMenuPerson, setInteractionMenuPerson] = useState(null);
  const [interactionMenuPosition, setInteractionMenuPosition] = useState({ x: 0, y: 0 });

  const [daysThreshold, setDaysThreshold] = useState(3); // Default threshold for unanswered emails
  const [followUpTimeFilter, setFollowUpTimeFilter] = useState("today"); // past, today, future
  const [followUpAssignmentFilter, setFollowUpAssignmentFilter] = useState("me"); // all, me, team
  const [followUpFilters, setFollowUpFilters] = useState({
    statuses: [],
    assignedTo: [],
    searchQuery: ""
  });
  const [isFollowUpFilterOpen, setIsFollowUpFilterOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeEmailTab, setActiveEmailTab] = useState("inbox"); // "inbox", "sent", or "drafts"
  const [inboxFilter, setInboxFilter] = useState("needs-response"); // "all" or "needs-response"
  const [sentFilter, setSentFilter] = useState("needs-response"); // "all" or "needs-response"
  const [emailPageToken, setEmailPageToken] = useState(null);
  const [hasMoreEmails, setHasMoreEmails] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMeetingsCollapsed, setIsMeetingsCollapsed] = useState(() => {
    // Collapse on small screens (< 1024px), expand on large screens
    return window.innerWidth < 1024;
  });
  
  // Email action states
  const [replyEmail, setReplyEmail] = useState(null);
  const [replyAllMode, setReplyAllMode] = useState(false);
  const [moveEmail, setMoveEmail] = useState(null);
  const [gmailLabels, setGmailLabels] = useState([]);
  const [addPersonEmail, setAddPersonEmail] = useState(null);
  const [emailPeopleMap, setEmailPeopleMap] = useState({}); // Maps email addresses to person objects
  const [dismissedEmailIds, setDismissedEmailIds] = useState(new Set()); // Set of dismissed email IDs

  // Process preloaded data directly without API calls
  const processPreloadedData = async (data) => {
    try {
      console.log("[ToDo] Processing preloaded data:", data);
      
      // Process calendar events
      const events = data.calendar || [];
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const meetings = events.filter(event => {
        const start = new Date(event.start.dateTime || event.start.date);
        return start >= today && start < tomorrow;
      }).sort((a, b) => new Date(a.start.dateTime || a.start.date) - new Date(b.start.dateTime || b.start.date));
      
      // Process emails
      const emails = data.emails || [];
      const people = data.people || [];
      
      // Create email to person map
      const emailMap = {};
      people.forEach(person => {
        if (person.email) {
          emailMap[person.email.toLowerCase().trim()] = person;
        }
      });
      setEmailPeopleMap(emailMap);
      
      // Get dismissed emails
      const dismissedResponse = await api.get("/google/gmail/dismissed").catch(() => ({ data: [] }));
      const currentDismissedIds = new Set(dismissedResponse.data || []);
      setDismissedEmailIds(currentDismissedIds);
      
      // Process inbox, sent, and draft emails
      const cutoff = Date.now() - daysThreshold * 24 * 60 * 60 * 1000;
      const allSentEmails = [];
      const allReceivedEmails = [];
      const unrespondedSentEmails = [];
      const unrespondedReceivedEmails = [];
      const draftEmails = [];
      
      emails.forEach(email => {
        if (currentDismissedIds.has(email.id)) return;
        
        // Drafts don't need time filtering
        if (email.labelIds?.includes("DRAFT")) {
          draftEmails.push(email);
        } else if (email.labelIds?.includes("SENT")) {
          // All sent emails
          allSentEmails.push(email);
          // Sent emails that need response (within threshold)
          if (Number(email.internalDate) >= cutoff) {
            unrespondedSentEmails.push(email);
          }
        } else if (email.labelIds?.includes("INBOX")) {
          // All inbox emails
          allReceivedEmails.push(email);
          // Inbox emails that need response (within threshold)
          if (Number(email.internalDate) >= cutoff) {
            unrespondedReceivedEmails.push(email);
          }
        }
      });
      
      // Get follow-ups from people data
      const followUps = people
        .filter(p => p.follow_up_date)
        .map(p => ({
          id: p.id,
          name: p.name,
          company: p.company,
          follow_up_date: p.follow_up_date,
          next_step: p.next_step,
          status: p.status,
          assigned_to: p.assigned_to,
        }))
        .sort((a, b) => new Date(a.follow_up_date) - new Date(b.follow_up_date));
      
      setTasks({
        meetings,
        allSentEmails,
        allReceivedEmails,
        unrespondedSentEmails,
        unrespondedReceivedEmails,
        draftEmails,
        followUps,
      });
      
      console.log("[ToDo] Preloaded data processed successfully");
    } catch (error) {
      console.error("[ToDo] Error processing preloaded data:", error);
    }
  };

  // Data load - runs on mount and whenever returning to /todo page or threshold changes
  useEffect(() => {
    console.log("[ToDo] Component mounted or location changed:", location.pathname);
    
    // Always fetch fresh data when on /todo page
    const loadData = async () => {
      console.log("[ToDo] Fetching fresh data");
      setLoading(true);
      
      // Fetch all data in parallel
      await Promise.all([
        fetchUsers(),
        fetchGmailLabels(),
        fetchDismissedEmails(),
        fetchAllTasks()
      ]);
      
      setLoading(false);
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, daysThreshold]);

  const fetchUsers = async () => {
    try {
      if (currentUser && currentUser.role === "team lead") {
        const response = await api.get(`/team-users?teamLeadId=${currentUser.id}`);
        setUsers(response.data);
      } else {
        const response = await api.get("/userinfo");
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchGmailLabels = async () => {
    try {
      const response = await api.get("/google/gmail/labels");
      setGmailLabels(response.data || []);
    } catch (error) {
      console.error("[fetchGmailLabels] Error fetching labels:", error);
      setGmailLabels([]);
    }
  };

  const fetchDismissedEmails = async () => {
    try {
      const response = await api.get("/google/gmail/dismissed");
      const dismissedIds = response.data || [];
      setDismissedEmailIds(new Set(dismissedIds));
    } catch (error) {
      console.error("[fetchDismissedEmails] Error fetching dismissed emails:", error);
      setDismissedEmailIds(new Set());
    }
  };

  const extractEmail = (headerText) => {
    if (!headerText) return "";
    const match = headerText.match(/<([^>]+)>/);
    return match ? match[1].trim().toLowerCase() : headerText.trim().toLowerCase();
  };

  const extractEmails = (headerText) => {
    if (!headerText) return [];
    // Split by comma for multiple recipients
    return headerText.split(',').map(addr => {
      const match = addr.match(/<([^>]+)>/);
      return match ? match[1].trim().toLowerCase() : addr.trim().toLowerCase();
    });
  };

  const hasMultipleRecipients = (email) => {
    const headers = email.payload?.headers || [];
    const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
    
    const toHeader = getHeader("To");
    const ccHeader = getHeader("Cc");
    const bccHeader = getHeader("Bcc");
    const fromHeader = getHeader("From");
    
    // Get all unique email addresses from To, CC, and BCC
    const allRecipients = [
      ...extractEmails(toHeader),
      ...extractEmails(ccHeader),
      ...extractEmails(bccHeader),
      extractEmail(fromHeader)
    ].filter(email => email); // Remove empty strings
    
    // Get unique recipients
    const uniqueRecipients = [...new Set(allRecipients)];
    
    // If there are more than 2 unique recipients (more than just sender and receiver), show Reply All
    // OR if there's more than 1 person in To/CC combined
    const toAndCcCount = [...extractEmails(toHeader), ...extractEmails(ccHeader)].filter(e => e).length;
    
    return uniqueRecipients.length > 2 || toAndCcCount > 1;
  };

  const checkEmailsInPeople = async (emails) => {
    try {
      // Get all people
      const response = await api.get("/people");
      const people = response.data || [];
      
      // Create a map of email addresses to person objects
      const peopleMap = {};
      people.forEach(person => {
        if (person.email) {
          const normalizedEmail = person.email.toLowerCase().trim();
          peopleMap[normalizedEmail] = person;
        }
      });
      
      setEmailPeopleMap(peopleMap);
    } catch (error) {
      console.error("[checkEmailsInPeople] Error checking emails:", error);
    }
  };

  const fetchAllTasks = async () => {
    try {
      await Promise.all([
        fetchTodaysMeetings(),
        fetchUnrespondedEmails(),
        fetchFollowUps()
      ]);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      showToast?.("Error loading tasks", "error");
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setEmailPageToken(null);
    setHasMoreEmails(false);
    try {
      await Promise.all([
        fetchUsers(),
        fetchGmailLabels(),
        fetchDismissedEmails(),
        fetchAllTasks()
      ]);
      showToast?.("Tasks refreshed", "success");
    } catch (error) {
      console.error("Error refreshing:", error);
      showToast?.("Error refreshing tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreEmails = async () => {
    if (!emailPageToken || loadingMore) return;
    
    setLoadingMore(true);
    try {
      await fetchUnrespondedEmails(true, emailPageToken);
      showToast && showToast("Loaded more emails", "success");
    } catch (error) {
      console.error("[handleLoadMoreEmails] Error:", error);
      showToast && showToast("Failed to load more emails", "error");
    }
    setLoadingMore(false);
  };

  const fetchTodaysMeetings = async () => {
    try {
      const response = await api.get("/google/calendar/events");
      const events = response.data || [];
      
      // Filter for today's meetings
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todaysMeetings = events.filter(event => {
        // Handle both dateTime (timed events) and date (all-day events)
        const startDateTime = event.start?.dateTime || event.start?.date;
        if (!startDateTime) {
          return false;
        }
        
        const eventDate = new Date(startDateTime);
        const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        
        const isToday = eventDay.getTime() === today.getTime();
        
        return isToday;
      });

      setTasks(prev => ({ ...prev, meetings: todaysMeetings }));
    } catch (error) {
      console.error("Error fetching meetings:", error);
      // Silently fail if Google Calendar is not connected
      setTasks(prev => ({ ...prev, meetings: [] }));
    }
  };

  const fetchUnrespondedEmails = async (append = false, pageToken = null) => {
    try {
      // Fetch dismissed emails first to ensure we have the latest list
      const dismissedResponse = await api.get("/google/gmail/dismissed");
      const currentDismissedIds = new Set(dismissedResponse.data || []);
      
      // Update state for future use
      setDismissedEmailIds(currentDismissedIds);
      
      // Build API URL with parameters
      let apiUrl = "/google/gmail/messages?maxResults=1000";
      
      // Only apply daysLimit for "needs-response" filter
      if (inboxFilter === "needs-response" || sentFilter === "needs-response") {
        apiUrl += `&daysLimit=30`;
      }
      // For "all" filter, no daysLimit parameter = fetch all emails ever
      
      if (pageToken) {
        apiUrl += `&pageToken=${encodeURIComponent(pageToken)}`;
      }
      
      console.log(`[fetchUnrespondedEmails] Fetching with URL: ${apiUrl}, append: ${append}`);
      const response = await api.get(apiUrl);
      
      // Handle new response format
      const responseData = response.data;
      const emails = responseData.messages || responseData || [];
      const nextPageToken = responseData.nextPageToken || null;
      const hasMore = responseData.hasMore || false;
      
      console.log(`[fetchUnrespondedEmails] Fetched ${emails.length} emails, hasMore: ${hasMore}`);
      
      setEmailPageToken(nextPageToken);
      setHasMoreEmails(hasMore);
      
      // Check which emails are in the people database
      await checkEmailsInPeople(emails);
      
      const thresholdDate = new Date();
      thresholdDate.setDate(thresholdDate.getDate() - daysThreshold);

      // Group emails by thread
      const threadMap = new Map();
      emails.forEach(email => {
        const threadId = email.threadId;
        if (!threadMap.has(threadId)) {
          threadMap.set(threadId, []);
        }
        threadMap.get(threadId).push(email);
      });

      const sentUnresponded = [];
      const receivedUnresponded = [];
      const allSentEmails = [];
      const allReceivedEmails = [];
      const draftEmails = [];

      // First, extract all draft emails (they don't need thread processing)
      emails.forEach(email => {
        if (email.labelIds?.includes("DRAFT") && !currentDismissedIds.has(email.id)) {
          const headers = email.payload?.headers || [];
          const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
          
          draftEmails.push({
            ...email,
            subject: getHeader("Subject") || "(No Subject)",
            to: getHeader("To"),
            from: getHeader("From"),
            date: new Date(Number(email.internalDate)),
          });
        }
      });

      // Process each thread
      threadMap.forEach((threadEmails, threadId) => {
        // Sort by date
        threadEmails.sort((a, b) => Number(a.internalDate) - Number(b.internalDate));

        // Find the last sent and last received email
        let lastSent = null;
        let lastReceived = null;

        threadEmails.forEach(email => {
          const isSent = email.labelIds?.includes("SENT");
          const isInbox = email.labelIds?.includes("INBOX");

          if (isSent) {
            lastSent = email;
          }
          if (isInbox && !isSent) {
            lastReceived = email;
          }
        });

        // Check if last sent email needs a response
        if (lastSent) {
          const lastSentDate = new Date(Number(lastSent.internalDate));
          const lastReceivedDate = lastReceived ? new Date(Number(lastReceived.internalDate)) : null;

          const headers = lastSent.payload?.headers || [];
          const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
          
          const toHeader = getHeader("To");
          const toEmail = extractEmail(toHeader);
          
          // Filter out emails sent to @rthree.io domain (internal emails)
          if (!toEmail.endsWith('@rthree.io')) {
            // Store full email object with display fields
            const email = {
              ...lastSent, // Keep full email object for actions
              subject: getHeader("Subject") || "(No Subject)",
              to: toHeader,
              from: getHeader("From"),
              date: lastSentDate,
            };
            
            // Add to all sent emails
            allSentEmails.push(email);
            
            // If last sent is older than threshold and no response received after it
            if (lastSentDate < thresholdDate && 
                (!lastReceivedDate || lastReceivedDate < lastSentDate)) {
              sentUnresponded.push(email);
            }
          }
        }

        // Check if last received email needs a response
        if (lastReceived) {
          const lastReceivedDate = new Date(Number(lastReceived.internalDate));
          const lastSentDate = lastSent ? new Date(Number(lastSent.internalDate)) : null;

          const headers = lastReceived.payload?.headers || [];
          const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
          
          const fromHeader = getHeader("From");
          const fromEmail = extractEmail(fromHeader);
          
          // Filter out emails from @rthree.io domain (internal emails)
          if (!fromEmail.endsWith('@rthree.io')) {
            // Store full email object with display fields
            const email = {
              ...lastReceived, // Keep full email object for actions
              subject: getHeader("Subject") || "(No Subject)",
              from: fromHeader,
              to: getHeader("To"),
              date: lastReceivedDate,
            };
            
            // Add to all received emails
            allReceivedEmails.push(email);
            
            // If last received is older than threshold and no response sent after it
            if (lastReceivedDate < thresholdDate && 
                (!lastSentDate || lastSentDate < lastReceivedDate)) {
              receivedUnresponded.push(email);
            }
          }
        }
      });

      // Filter out dismissed emails (using the freshly fetched currentDismissedIds)
      const filteredSentUnresponded = sentUnresponded.filter(email => !currentDismissedIds.has(email.id));
      const filteredReceivedUnresponded = receivedUnresponded.filter(email => !currentDismissedIds.has(email.id));
      const filteredAllSent = allSentEmails.filter(email => !currentDismissedIds.has(email.id));
      const filteredAllReceived = allReceivedEmails.filter(email => !currentDismissedIds.has(email.id));

      // If appending (loading more), merge with existing emails
      if (append) {
        setTasks(prev => ({ 
          ...prev, 
          allSentEmails: [...prev.allSentEmails, ...filteredAllSent],
          allReceivedEmails: [...prev.allReceivedEmails, ...filteredAllReceived],
          unrespondedSentEmails: [...prev.unrespondedSentEmails, ...filteredSentUnresponded],
          unrespondedReceivedEmails: [...prev.unrespondedReceivedEmails, ...filteredReceivedUnresponded],
          draftEmails: [...prev.draftEmails, ...draftEmails]
        }));
      } else {
        // Replace all emails
        setTasks(prev => ({ 
          ...prev, 
          allSentEmails: filteredAllSent,
          allReceivedEmails: filteredAllReceived,
          unrespondedSentEmails: filteredSentUnresponded,
          unrespondedReceivedEmails: filteredReceivedUnresponded,
          draftEmails: draftEmails
        }));
      }
    } catch (error) {
      console.error("[fetchUnrespondedEmails] Error fetching emails:", error);
      console.error("[fetchUnrespondedEmails] Error details:", error.response?.data || error.message);
      // Silently fail if Gmail is not connected
      setTasks(prev => ({ 
        ...prev, 
        allSentEmails: [],
        allReceivedEmails: [],
        unrespondedSentEmails: [],
        unrespondedReceivedEmails: [],
        draftEmails: []
      }));
    }
  };

  const fetchFollowUps = async () => {
    try {
      // Fetch people with appropriate view based on user role
      // For team leads and admins, fetch all or team view to enable filtering
      const isTeamLeadOrAdmin = currentUser?.role === "team lead" || currentUser?.role === "admin";
      const viewParam = isTeamLeadOrAdmin ? "view=all" : "";
      const response = await api.get(`/people${viewParam ? `?${viewParam}` : ""}`);
      const people = response.data || [];
      
      // Get all follow-ups (not just past ones, we'll filter by time in the UI)
      const allFollowUps = people.filter(person => {
        return person.follow_up_date !== null && person.follow_up_date !== undefined;
      });

      // Sort by follow_up_date, oldest first (most overdue first)
      allFollowUps.sort((a, b) => {
        const dateA = new Date(a.follow_up_date);
        const dateB = new Date(b.follow_up_date);
        return dateA - dateB;
      });

      setTasks(prev => ({ ...prev, followUps: allFollowUps }));
    } catch (error) {
      console.error("Error fetching follow-ups:", error);
      setTasks(prev => ({ ...prev, followUps: [] }));
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (date) => {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${dateStr} at ${timeStr}`;
  };

  const getDaysAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffTime = Math.abs(now - then);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTotalTaskCount = () => {
    return tasks.meetings.length + 
           tasks.unrespondedSentEmails.length + 
           tasks.unrespondedReceivedEmails.length + 
           tasks.draftEmails.length + 
           filteredFollowUps.length;
  };

  const isTeamLead = currentUser?.role === "team lead" || currentUser?.role === "admin";

  // Filter follow-ups based on active tab and filters
  const filteredFollowUps = useMemo(() => {
    let filtered = [...tasks.followUps];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Apply time filter
    switch (followUpTimeFilter) {
      case "past":
        filtered = filtered.filter(person => {
          const followUpDate = new Date(person.follow_up_date);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate < today;
        });
        break;
      case "today":
        filtered = filtered.filter(person => {
          const followUpDate = new Date(person.follow_up_date);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate.getTime() === today.getTime();
        });
        break;
      case "future":
        filtered = filtered.filter(person => {
          const followUpDate = new Date(person.follow_up_date);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate >= tomorrow;
        });
        break;
      default:
        break;
    }

    // Apply assignment filter
    switch (followUpAssignmentFilter) {
      case "me":
        filtered = filtered.filter(person => person.assigned_to === currentUser?.id);
        break;
      case "team":
        if (isTeamLead) {
          // Show team members' follow-ups (not including the lead's own)
          const teamMemberIds = users.filter(u => u.team === currentUser?.id && u.id !== currentUser?.id).map(u => u.id);
          filtered = filtered.filter(person => teamMemberIds.includes(person.assigned_to));
        }
        break;
      case "all":
      default:
        // No assignment filter - show all
        break;
    }

    // Apply status filter
    if (followUpFilters.statuses.length > 0) {
      filtered = filtered.filter(person => 
        followUpFilters.statuses.includes(person.status)
      );
    }

    // Apply assigned to filter
    if (followUpFilters.assignedTo.length > 0) {
      filtered = filtered.filter(person => 
        followUpFilters.assignedTo.includes(person.assigned_to)
      );
    }

    // Apply search filter
    if (followUpFilters.searchQuery) {
      const query = followUpFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(person => 
        person.name?.toLowerCase().includes(query) ||
        person.company?.toLowerCase().includes(query) ||
        person.email?.toLowerCase().includes(query) ||
        person.phone?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [tasks.followUps, followUpTimeFilter, followUpAssignmentFilter, followUpFilters, currentUser, users, isTeamLead]);

  // Separate filter for calendar - always show today's follow-ups regardless of other filters
  const calendarFollowUps = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return tasks.followUps.filter(f => {
      if (!f.follow_up_date) return false;
      const followUpDate = new Date(f.follow_up_date);
      followUpDate.setHours(0, 0, 0, 0);
      return followUpDate.getTime() === today.getTime();
    });
  }, [tasks.followUps]);

  const handlePersonSave = async (updatedPerson) => {
    try {
      // Update the person in the backend
      await api.put(`/people/${updatedPerson.id}`, updatedPerson);
      
      // Update local state
      setTasks(prev => ({
        ...prev,
        followUps: prev.followUps.map(person => 
          person.id === updatedPerson.id ? updatedPerson : person
        )
      }));
      
      // Update selected person only if the panel is still open
      // Check if selectedPerson is still set (user hasn't closed the panel)
      setSelectedPerson(prev => {
        if (prev && prev.id === updatedPerson.id) {
          console.log('[ToDo] handlePersonSave: Panel still open, updating selectedPerson after save:', updatedPerson.name);
          return updatedPerson;
        } else {
          console.log('[ToDo] handlePersonSave: Panel was closed or different person, not reopening');
          return prev;
        }
      });
      
      showToast && showToast("Person updated successfully", "success");
      
      // Refresh the badge count (in case follow-up dates changed)
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      
      return true;
    } catch (error) {
      console.error("Error updating person:", error);
      showToast && showToast("Failed to update person", "error");
      return false;
    }
  };

  const handleInteractionSubmit = async (payload) => {
    try {
      await api.post("/interactions", payload);
      // Refetch follow-ups to update the list
      fetchFollowUps();
      setInteractionMenuPerson(null);
      showToast && showToast("Interaction added successfully", "success");
    } catch (error) {
      console.error("Error adding interaction:", error);
      showToast && showToast("Failed to add interaction", "error");
    }
  };

  const handleFollowUpRightClick = (e, person) => {
    e.preventDefault();
    setInteractionMenuPosition({ x: e.clientX, y: e.clientY });
    setInteractionMenuPerson(person);
  };

  // Email action handlers
  const handleEmailReply = (email, replyAll = false) => {
    setReplyAllMode(replyAll);
    setReplyEmail(email);
  };

  const handleEmailArchive = async (email) => {
    try {
      await api.post("/google/gmail/archive", { messageId: email.id });
      showToast?.("Email archived", "success");
      // Remove email from the list
      setTasks(prev => ({
        ...prev,
        allSentEmails: prev.allSentEmails.filter(e => e.id !== email.id),
        allReceivedEmails: prev.allReceivedEmails.filter(e => e.id !== email.id),
        unrespondedSentEmails: prev.unrespondedSentEmails.filter(e => e.id !== email.id),
        unrespondedReceivedEmails: prev.unrespondedReceivedEmails.filter(e => e.id !== email.id),
        draftEmails: prev.draftEmails.filter(e => e.id !== email.id),
      }));
    } catch (error) {
      console.error("Error archiving email:", error);
      showToast?.("Failed to archive email", "error");
    }
  };

  const handleEmailTrash = async (email) => {
    try {
      await api.post("/google/gmail/trash", { messageId: email.id });
      showToast?.("Email moved to trash", "success");
      // Remove email from the list
      setTasks(prev => ({
        ...prev,
        allSentEmails: prev.allSentEmails.filter(e => e.id !== email.id),
        allReceivedEmails: prev.allReceivedEmails.filter(e => e.id !== email.id),
        unrespondedSentEmails: prev.unrespondedSentEmails.filter(e => e.id !== email.id),
        unrespondedReceivedEmails: prev.unrespondedReceivedEmails.filter(e => e.id !== email.id),
        draftEmails: prev.draftEmails.filter(e => e.id !== email.id),
      }));
    } catch (error) {
      console.error("Error trashing email:", error);
      showToast?.("Failed to trash email", "error");
    }
  };

  const handleEmailDismiss = async (email) => {
    try {
      // Save to database
      await api.post("/google/gmail/dismiss", { emailId: email.id });
      
      // Add to dismissed set
      setDismissedEmailIds(prev => new Set([...prev, email.id]));
      
      // Remove from the to-do list
      setTasks(prev => ({
        ...prev,
        allSentEmails: prev.allSentEmails.filter(e => e.id !== email.id),
        allReceivedEmails: prev.allReceivedEmails.filter(e => e.id !== email.id),
        unrespondedSentEmails: prev.unrespondedSentEmails.filter(e => e.id !== email.id),
        unrespondedReceivedEmails: prev.unrespondedReceivedEmails.filter(e => e.id !== email.id),
        draftEmails: prev.draftEmails.filter(e => e.id !== email.id),
      }));
      
      showToast?.("Email marked as done", "success");
      
      // Refresh the badge count
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error("[handleEmailDismiss] Error dismissing email:", error);
      showToast?.("Failed to mark email as done", "error");
    }
  };

  const handleEmailMove = (email) => {
    setMoveEmail(email);
  };

  const handleSendEmail = async (emailData) => {
    try {
      await api.post("/google/gmail/send", emailData);
      // Remove from unanswered list since we replied
      setTasks(prev => ({
        ...prev,
        allSentEmails: prev.allSentEmails.filter(e => e.id !== replyEmail.id),
        allReceivedEmails: prev.allReceivedEmails.filter(e => e.id !== replyEmail.id),
        unrespondedSentEmails: prev.unrespondedSentEmails.filter(e => e.id !== replyEmail.id),
        unrespondedReceivedEmails: prev.unrespondedReceivedEmails.filter(e => e.id !== replyEmail.id),
        draftEmails: prev.draftEmails.filter(e => e.id !== replyEmail.id),
      }));
    } catch (error) {
      throw error;
    }
  };

  const handleMoveEmailToLabel = async (moveData) => {
    try {
      await api.post("/google/gmail/label", moveData);
      // Remove from current list
      setTasks(prev => ({
        ...prev,
        allSentEmails: prev.allSentEmails.filter(e => e.id !== moveEmail.id),
        allReceivedEmails: prev.allReceivedEmails.filter(e => e.id !== moveEmail.id),
        unrespondedSentEmails: prev.unrespondedSentEmails.filter(e => e.id !== moveEmail.id),
        unrespondedReceivedEmails: prev.unrespondedReceivedEmails.filter(e => e.id !== moveEmail.id),
        draftEmails: prev.draftEmails.filter(e => e.id !== moveEmail.id),
      }));
    } catch (error) {
      throw error;
    }
  };

  const getEmailPerson = (email, isSent = false) => {
    // For sent emails, check the "To" field
    // For received emails, check the "From" field
    const emailAddress = isSent 
      ? extractEmail(email.to || "") 
      : extractEmail(email.from || "");
    
    return emailPeopleMap[emailAddress] || null;
  };

  const handleEmailCardClick = (email, isSent) => {
    console.log('[ToDo] handleEmailCardClick called', { emailId: email.id, isSent, emailSubject: email.subject });
    const person = getEmailPerson(email, isSent);
    console.log('[ToDo] Person found:', person ? { id: person.id, name: person.name } : 'none');
    if (person) {
      console.log('[ToDo] Opening PeopleDetails for person:', person.name);
      setSelectedPerson(person);
    }
  };

  const handleEmailRightClick = (e, email, isSent) => {
    e.preventDefault();
    const person = getEmailPerson(email, isSent);
    
    if (!person) {
      // Person doesn't exist, open add person modal
      setAddPersonEmail(email);
    }
  };

  const handleAddPerson = async (personData) => {
    try {
      // Extract assignments from personData
      const { assignments, ...personFields } = personData;
      
      // Create the person first
      const response = await api.post("/people", personFields);
      const newPerson = response.data;
      
      // Create assignments if provided
      if (assignments) {
        const assignmentPromises = [];
        
        // Add primary assignments
        if (assignments.primary && assignments.primary.length > 0) {
          assignments.primary.forEach(userId => {
            assignmentPromises.push(
              api.post("/people-assignments", {
                person_id: newPerson.id,
                user_id: userId,
                assignment_type: "primary"
              })
            );
          });
        }
        
        // Add secondary assignments
        if (assignments.secondary && assignments.secondary.length > 0) {
          assignments.secondary.forEach(userId => {
            assignmentPromises.push(
              api.post("/people-assignments", {
                person_id: newPerson.id,
                user_id: userId,
                assignment_type: "secondary"
              })
            );
          });
        }
        
        // Wait for all assignments to be created
        await Promise.all(assignmentPromises);
      }
      
      // Update the emailPeopleMap with the new person
      const normalizedEmail = personFields.email.toLowerCase().trim();
      setEmailPeopleMap(prev => ({
        ...prev,
        [normalizedEmail]: newPerson
      }));
      
      showToast?.("Person added successfully", "success");
      
      // Optionally refresh follow-ups in case this affects any
      fetchFollowUps();
    } catch (error) {
      console.error("[handleAddPerson] Error adding person:", error);
      showToast?.("Failed to add person: " + (error.response?.data?.error || error.message), "error");
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="todo-container">
        <div className="loading-message">
          <FiClock size={32} />
          <p>Loading your tasks for today...</p>
        </div>
      </div>
    );
  }

  const totalTasks = getTotalTaskCount();

  return (
    <div className="todo-container">
      <div className="todo-header">
        <div className="todo-header-content">
          <h1 className="todo-title">Today's To-Do</h1>
          <p className="todo-subtitle">
            {totalTasks === 0 
              ? "You're all caught up! 🎉" 
              : `${totalTasks} task${totalTasks !== 1 ? 's' : ''} requiring your attention`}
          </p>
        </div>
        
        <div className="todo-controls">
          <button onClick={handleRefresh} className="refresh-button">
            <FiClock /> Refresh
          </button>
        </div>
      </div>

      <div className="todo-sections">
        {/* Left Column - Email Responses and Follow-ups */}
        <div className="todo-sections-left">
          {/* Email Responses - Combined with Tabs */}
          <section className="todo-section">
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiMail className="section-icon" />
              <h2 className="section-title">Email Responses</h2>
              <span className="task-count">
                {activeEmailTab === "inbox" ? 
                  (inboxFilter === "all" ? tasks.allReceivedEmails.length : tasks.unrespondedReceivedEmails.length) : 
                 activeEmailTab === "sent" ? 
                  (sentFilter === "all" ? tasks.allSentEmails.length : tasks.unrespondedSentEmails.length) : 
                 tasks.draftEmails.length}
              </span>
            </div>
            
            {/* Show threshold control only for Inbox/Sent when "Needs Response" is active */}
            {(activeEmailTab === "inbox" || activeEmailTab === "sent") && 
             ((activeEmailTab === "inbox" && inboxFilter === "needs-response") || 
              (activeEmailTab === "sent" && sentFilter === "needs-response")) && (
              <label className="threshold-control" style={{ 
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.85rem'
              }}>
                <span>Threshold:</span>
                <select 
                  value={daysThreshold} 
                  onChange={(e) => setDaysThreshold(Number(e.target.value))}
                  className="threshold-select"
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.85rem',
                    borderRadius: '4px',
                    border: '1px solid var(--border)',
                    background: 'var(--input-bg)',
                    color: 'var(--input-text)'
                  }}
                >
                  <option value={1}>1 day</option>
                  <option value={2}>2 days</option>
                  <option value={3}>3 days</option>
                  <option value={5}>5 days</option>
                  <option value={7}>7 days</option>
                </select>
              </label>
            )}
          </div>

          {/* Email Tabs */}
          <div className="email-tabs">
            <button 
              className={`tab-button ${activeEmailTab === "inbox" ? "active" : ""}`}
              onClick={() => setActiveEmailTab("inbox")}
            >
              Inbox
              <span className="tab-count">{inboxFilter === "all" ? tasks.allReceivedEmails.length : tasks.unrespondedReceivedEmails.length}</span>
            </button>
            <button 
              className={`tab-button ${activeEmailTab === "sent" ? "active" : ""}`}
              onClick={() => setActiveEmailTab("sent")}
            >
              Sent
              <span className="tab-count">{sentFilter === "all" ? tasks.allSentEmails.length : tasks.unrespondedSentEmails.length}</span>
            </button>
            <button 
              className={`tab-button ${activeEmailTab === "drafts" ? "active" : ""}`}
              onClick={() => setActiveEmailTab("drafts")}
            >
              Drafts
              <span className="tab-count">{tasks.draftEmails.length}</span>
            </button>
          </div>

          {/* Filter Buttons for Inbox and Sent */}
          {(activeEmailTab === "inbox" || activeEmailTab === "sent") && (
            <div className="email-filter-buttons" style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginBottom: '1rem',
              padding: '0.5rem',
              background: 'var(--hover-bg)',
              borderRadius: '6px'
            }}>
              <button 
                className={`filter-button ${activeEmailTab === "inbox" ? (inboxFilter === "all" ? "active" : "") : (sentFilter === "all" ? "active" : "")}`}
                onClick={() => activeEmailTab === "inbox" ? setInboxFilter("all") : setSentFilter("all")}
                style={{
                  flex: 1,
                  padding: '0.4rem 0.75rem',
                  background: (activeEmailTab === "inbox" ? inboxFilter === "all" : sentFilter === "all") ? 'var(--primary)' : 'transparent',
                  color: (activeEmailTab === "inbox" ? inboxFilter === "all" : sentFilter === "all") ? 'var(--button-text)' : 'var(--text)',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                All
              </button>
              <button 
                className={`filter-button ${activeEmailTab === "inbox" ? (inboxFilter === "needs-response" ? "active" : "") : (sentFilter === "needs-response" ? "active" : "")}`}
                onClick={() => activeEmailTab === "inbox" ? setInboxFilter("needs-response") : setSentFilter("needs-response")}
                style={{
                  flex: 1,
                  padding: '0.4rem 0.75rem',
                  background: (activeEmailTab === "inbox" ? inboxFilter === "needs-response" : sentFilter === "needs-response") ? 'var(--primary)' : 'transparent',
                  color: (activeEmailTab === "inbox" ? inboxFilter === "needs-response" : sentFilter === "needs-response") ? 'var(--button-text)' : 'var(--text)',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Needs Response
              </button>
            </div>
          )}

          {/* Inbox Tab Content */}
          {activeEmailTab === "inbox" && (
            <>
              {(inboxFilter === "all" ? tasks.allReceivedEmails : tasks.unrespondedReceivedEmails).length === 0 ? (
                <div className="empty-state">
                  <p>{inboxFilter === "all" ? "No inbox emails" : "All inbox emails have been responded to"}</p>
                </div>
              ) : (
                <div className="task-list">
                  {(inboxFilter === "all" ? tasks.allReceivedEmails : tasks.unrespondedReceivedEmails).map(email => {
                const person = getEmailPerson(email, false); // false = received email
                const hasPerson = !!person;
                const showReplyAll = hasMultipleRecipients(email);
                
                return (
                <Card 
                  key={email.id} 
                  className={`task-card ${hasPerson ? 'has-person' : ''}`}
                  onContextMenu={(e) => handleEmailRightClick(e, email, false)}
                >
                  <div 
                    className="task-content"
                    onClick={hasPerson ? (e) => {
                      console.log('[ToDo] Inbox email card content clicked, has person:', hasPerson);
                      handleEmailCardClick(email, false);
                    } : undefined}
                    style={{ cursor: hasPerson ? 'pointer' : 'default' }}
                  >
                    <div className="task-main">
                      <div className="task-title-row">
                        <h3 className="task-title">
                          {hasPerson && <FiUser className="person-indicator" title={`In People: ${person.name}`} />}
                          {email.subject}
                        </h3>
                      </div>
                      <p className="task-meta">
                        <span>From: {email.from}</span>
                        {!hasPerson && (
                          <button
                            className="add-person-inline-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const fromEmail = extractEmail(email.from);
                              const fromName = email.from?.split('<')[0]?.trim() || email.from?.split('@')[0] || '';
                              setAddPersonEmail({ email: fromEmail, name: fromName, isSent: false });
                            }}
                            title="Add to people"
                          >
                            <FiUserPlus size={14} />
                          </button>
                        )}
                      </p>
                      <p className="task-snippet">{email.snippet}</p>
                      <p className="task-date">
                        <FiAlertCircle /> Received {getDaysAgo(email.date)} day{getDaysAgo(email.date) !== 1 ? 's' : ''} ago
                      </p>
                      <div className="email-actions">
                        <button 
                          className="email-action-btn reply"
                          onClick={(e) => { e.stopPropagation(); handleEmailReply(email, false); }}
                          title="Reply"
                        >
                          <FiCornerUpLeft /> Reply
                        </button>
                        {showReplyAll && (
                          <button 
                            className="email-action-btn reply-all"
                            onClick={(e) => { e.stopPropagation(); handleEmailReply(email, true); }}
                            title="Reply All"
                          >
                            <FiUserPlus /> Reply All
                          </button>
                        )}
                        <button 
                          className="email-action-btn done"
                          onClick={(e) => { e.stopPropagation(); handleEmailDismiss(email); }}
                          title="Mark as Done"
                        >
                          <FiCheck /> Done
                        </button>
                        <button 
                          className="email-action-btn archive"
                          onClick={(e) => { e.stopPropagation(); handleEmailArchive(email); }}
                          title="Archive"
                        >
                          <FiArchive />
                        </button>
                        <button 
                          className="email-action-btn move"
                          onClick={(e) => { e.stopPropagation(); handleEmailMove(email); }}
                          title="Move to..."
                        >
                          <FiFolder />
                        </button>
                        <button 
                          className="email-action-btn trash"
                          onClick={(e) => { e.stopPropagation(); handleEmailTrash(email); }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              )})}
                </div>
              )}
            </>
          )}

          {/* Sent Emails Tab Content */}
          {activeEmailTab === "sent" && (
            <>
              {(sentFilter === "all" ? tasks.allSentEmails : tasks.unrespondedSentEmails).length === 0 ? (
                <div className="empty-state">
                  <p>{sentFilter === "all" ? "No sent emails" : "All sent emails have been responded to"}</p>
                </div>
              ) : (
                <div className="task-list">
                  {(sentFilter === "all" ? tasks.allSentEmails : tasks.unrespondedSentEmails).map(email => {
                const person = getEmailPerson(email, true); // true = sent email
                const hasPerson = !!person;
                const showReplyAll = hasMultipleRecipients(email);
                
                return (
                <Card 
                  key={email.id} 
                  className={`task-card ${hasPerson ? 'has-person' : ''}`}
                  onContextMenu={(e) => handleEmailRightClick(e, email, true)}
                >
                  <div 
                    className="task-content"
                    onClick={hasPerson ? (e) => {
                      console.log('[ToDo] Sent email card content clicked, has person:', hasPerson);
                      handleEmailCardClick(email, true);
                    } : undefined}
                    style={{ cursor: hasPerson ? 'pointer' : 'default' }}
                  >
                    <div className="task-main">
                      <div className="task-title-row">
                        <h3 className="task-title">
                          {hasPerson && <FiUser className="person-indicator" title={`In People: ${person.name}`} />}
                          {email.subject}
                        </h3>
                      </div>
                      <p className="task-meta">
                        <span>To: {email.to}</span>
                        {!hasPerson && (
                          <button
                            className="add-person-inline-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const toEmail = extractEmail(email.to);
                              const toName = email.to?.split('@')[0] || '';
                              setAddPersonEmail({ email: toEmail, name: toName, isSent: true });
                            }}
                            title="Add to people"
                          >
                            <FiUserPlus size={14} />
                          </button>
                        )}
                      </p>
                      <p className="task-snippet">{email.snippet}</p>
                      <p className="task-date">
                        <FiAlertCircle /> Sent {getDaysAgo(email.date)} day{getDaysAgo(email.date) !== 1 ? 's' : ''} ago
                      </p>
                      <div className="email-actions">
                        <button 
                          className="email-action-btn reply"
                          onClick={(e) => { e.stopPropagation(); handleEmailReply(email, false); }}
                          title="Reply"
                        >
                          <FiCornerUpLeft /> Reply
                        </button>
                        {showReplyAll && (
                          <button 
                            className="email-action-btn reply-all"
                            onClick={(e) => { e.stopPropagation(); handleEmailReply(email, true); }}
                            title="Reply All"
                          >
                            <FiUserPlus /> Reply All
                          </button>
                        )}
                        <button 
                          className="email-action-btn done"
                          onClick={(e) => { e.stopPropagation(); handleEmailDismiss(email); }}
                          title="Mark as Done"
                        >
                          <FiCheck /> Done
                        </button>
                        <button 
                          className="email-action-btn archive"
                          onClick={(e) => { e.stopPropagation(); handleEmailArchive(email); }}
                          title="Archive"
                        >
                          <FiArchive />
                        </button>
                        <button 
                          className="email-action-btn move"
                          onClick={(e) => { e.stopPropagation(); handleEmailMove(email); }}
                          title="Move to..."
                        >
                          <FiFolder />
                        </button>
                        <button 
                          className="email-action-btn trash"
                          onClick={(e) => { e.stopPropagation(); handleEmailTrash(email); }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              )})}
                </div>
              )}
            </>
          )}

          {/* Drafts Tab Content */}
          {activeEmailTab === "drafts" && (
            <>
              {tasks.draftEmails.length === 0 ? (
                <div className="empty-state">
                  <p>No draft emails</p>
                </div>
              ) : (
                <div className="task-list">
                  {tasks.draftEmails.map(email => {
                const person = email.to ? getEmailPerson(email, true) : null; // Drafts might have "to" field
                const hasPerson = !!person;
                
                return (
                <Card 
                  key={email.id} 
                  className={`task-card draft-email ${hasPerson ? 'has-person' : ''}`}
                  onContextMenu={(e) => handleEmailRightClick(e, email, true)}
                >
                  <div 
                    className="task-content"
                    onClick={hasPerson ? (e) => {
                      console.log('[ToDo] Draft email card content clicked, has person:', hasPerson);
                      handleEmailCardClick(email, true);
                    } : undefined}
                    style={{ cursor: hasPerson ? 'pointer' : 'default' }}
                  >
                    <div className="task-main">
                      <div className="task-title-row">
                        <h3 className="task-title">
                          {hasPerson && <FiUser className="person-indicator" title={`In People: ${person.name}`} />}
                          {email.subject || "(No Subject)"}
                        </h3>
                      </div>
                      {email.to && (
                        <p className="task-meta">
                          <span>To: {email.to}</span>
                          {!hasPerson && (
                            <button
                              className="add-person-inline-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const toEmail = extractEmail(email.to);
                                const toName = email.to?.split('@')[0] || '';
                                setAddPersonEmail({ email: toEmail, name: toName, isSent: true });
                              }}
                              title="Add to people"
                            >
                              <FiUserPlus size={14} />
                            </button>
                          )}
                        </p>
                      )}
                      <p className="task-snippet">{email.snippet || "(Empty draft)"}</p>
                      <p className="task-date">
                        <FiAlertCircle /> Last modified {getDaysAgo(email.date)} day{getDaysAgo(email.date) !== 1 ? 's' : ''} ago
                      </p>
                      <div className="email-actions">
                        <button 
                          className="email-action-btn done"
                          onClick={(e) => { e.stopPropagation(); handleEmailDismiss(email); }}
                          title="Mark as Done"
                        >
                          <FiCheck /> Done
                        </button>
                        <button 
                          className="email-action-btn trash"
                          onClick={(e) => { e.stopPropagation(); handleEmailTrash(email); }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              )})}
                </div>
              )}
            </>
          )}

          {/* Load More Button */}
          {hasMoreEmails && (activeEmailTab === "inbox" || activeEmailTab === "sent") && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              padding: '1.5rem 0', 
              borderTop: '1px solid var(--border)',
              marginTop: '1rem'
            }}>
              <button 
                onClick={handleLoadMoreEmails}
                disabled={loadingMore}
                className="load-more-button"
                style={{
                  padding: '0.75rem 2rem',
                  background: loadingMore ? 'var(--hover-bg)' : 'var(--primary)',
                  color: loadingMore ? 'var(--text-muted)' : 'var(--button-text)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FiMail />
                {loadingMore ? 'Loading...' : 'Load More Emails'}
              </button>
            </div>
          )}
        </section>

        {/* Follow-ups from People */}
        <section className="todo-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2 className="section-title">Follow-ups Due</h2>
            <span className="task-count">{filteredFollowUps.length}</span>
          </div>

          {/* Tabs and Filter Menu */}
          <div className="followup-controls">
            {/* Time Period Tabs */}
            <div className="followup-tabs">
              <button 
                className={`tab-button ${followUpTimeFilter === "past" ? "active" : ""}`}
                onClick={() => setFollowUpTimeFilter("past")}
              >
                Past
              </button>
              <button 
                className={`tab-button ${followUpTimeFilter === "today" ? "active" : ""}`}
                onClick={() => setFollowUpTimeFilter("today")}
              >
                Today
              </button>
              <button 
                className={`tab-button ${followUpTimeFilter === "future" ? "active" : ""}`}
                onClick={() => setFollowUpTimeFilter("future")}
              >
                Future
              </button>
            </div>

            <span className="control-separator">|</span>

            {/* Assignment Tabs */}
            <div className="followup-tabs">
              <button 
                className={`tab-button ${followUpAssignmentFilter === "all" ? "active" : ""}`}
                onClick={() => setFollowUpAssignmentFilter("all")}
              >
                All
              </button>
              <button 
                className={`tab-button ${followUpAssignmentFilter === "me" ? "active" : ""}`}
                onClick={() => setFollowUpAssignmentFilter("me")}
              >
                Me
              </button>
              {isTeamLead && (
                <button 
                  className={`tab-button ${followUpAssignmentFilter === "team" ? "active" : ""}`}
                  onClick={() => setFollowUpAssignmentFilter("team")}
                >
                  Team
                </button>
              )}
            </div>

            <span className="control-separator">|</span>
            
            <div className="followup-controls-right">
              {/* Search */}
              <input
                type="text"
                placeholder="Search..."
                value={followUpFilters.searchQuery}
                onChange={(e) => setFollowUpFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="followup-search"
              />
              
              {/* Filter Button */}
              <button 
                className={`filter-toggle-button ${isFollowUpFilterOpen ? "active" : ""}`}
                onClick={() => setIsFollowUpFilterOpen(!isFollowUpFilterOpen)}
                title="More Filters"
              >
                <FiFilter />
              </button>
            </div>
          </div>

          {/* Filter Menu */}
          {isFollowUpFilterOpen && (
            <div className="followup-filter-menu">
              <h3>Filter Follow-ups</h3>
              
              <div className="filter-control">
                <label>Status:</label>
                <Select
                  isMulti
                  options={[
                    { value: "New", label: "New" },
                    { value: "Contacted", label: "Contacted" },
                    { value: "Scheduled", label: "Scheduled" },
                    { value: "Qualified", label: "Qualified" },
                    { value: "Proposal Sent", label: "Proposal Sent" },
                    { value: "Awaiting Info", label: "Awaiting Info" },
                    { value: "Not Interested", label: "Not Interested" },
                    { value: "Refused Appointment", label: "Refused Appointment" },
                    { value: "No Show", label: "No Show" },
                    { value: "Grant Writing", label: "Grant Writing" },
                    { value: "Application Sent", label: "Application Sent" },
                    { value: "Grant Awarded", label: "Grant Awarded" },
                    { value: "Grant Rejected", label: "Grant Rejected" },
                    { value: "Closed Won", label: "Closed Won" },
                    { value: "Closed Lost", label: "Closed Lost" }
                  ]}
                  value={followUpFilters.statuses.map(s => ({ value: s, label: s }))}
                  onChange={(selected) =>
                    setFollowUpFilters(prev => ({
                      ...prev,
                      statuses: selected ? selected.map(s => s.value) : []
                    }))
                  }
                  placeholder="Select statuses..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="filter-control">
                <label>Assigned To:</label>
                <Select
                  isMulti
                  options={users.map(user => ({
                    value: user.id,
                    label: `${user.first_name} ${user.last_name}`
                  }))}
                  value={users
                    .filter(user => followUpFilters.assignedTo.includes(user.id))
                    .map(user => ({
                      value: user.id,
                      label: `${user.first_name} ${user.last_name}`
                    }))
                  }
                  onChange={(selected) =>
                    setFollowUpFilters(prev => ({
                      ...prev,
                      assignedTo: selected ? selected.map(s => s.value) : []
                    }))
                  }
                  placeholder="Select users..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="actions">
                <button 
                  onClick={() => setFollowUpFilters({ statuses: [], assignedTo: [], searchQuery: "" })}
                >
                  Clear Filters
                </button>
                <button 
                  onClick={() => setIsFollowUpFilterOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          
          {filteredFollowUps.length === 0 ? (
            <div className="empty-state">
              <p>No follow-ups found for the selected filters</p>
            </div>
          ) : (
            <div className="task-list">
              {filteredFollowUps.map(person => (
                <Card 
                  key={person.id} 
                  className="task-card task-card-compact"
                  onClick={() => {
                    console.log('[ToDo] Follow-up card clicked, opening person:', person.name);
                    setSelectedPerson(person);
                  }}
                  onContextMenu={(e) => handleFollowUpRightClick(e, person)}
                >
                  <div className="task-content">
                    <div className="task-main">
                      <h3 className="task-title">{person.name}</h3>
                      <p className="task-meta">
                        {person.company && <span>{person.company}</span>}
                        {person.status && <span className="status-badge">{person.status}</span>}
                      </p>
                      <p className="task-date">
                        <FiCalendar /> {formatDateTime(person.follow_up_date)}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
        </div>

        {/* Right Column - Today's Meetings */}
        <div className="todo-sections-right">
          <section className="todo-section">
            <div 
              className="section-header collapsible" 
              onClick={() => setIsMeetingsCollapsed(!isMeetingsCollapsed)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiCalendar className="section-icon" />
                <h2 className="section-title">Today's Meetings</h2>
                <span className="task-count">
                  {tasks.meetings.filter(m => (m.attendees?.length || 0) > 1).length} meetings, {tasks.meetings.filter(m => (m.attendees?.length || 0) <= 1).length} tasks
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {isMeetingsCollapsed && tasks.meetings.length > 0 && (() => {
                  const sortedMeetings = [...tasks.meetings].sort((a, b) => 
                    new Date(a.start.dateTime || a.start.date) - new Date(b.start.dateTime || b.start.date)
                  );
                  const firstMeeting = sortedMeetings[0];
                  const lastMeeting = sortedMeetings[sortedMeetings.length - 1];
                  const formatTime = (meeting) => {
                    const date = new Date(meeting.start.dateTime || meeting.start.date);
                    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                  };
                  return (
                    <span className="meeting-time-range">
                      First at {formatTime(firstMeeting)}, last at {formatTime(lastMeeting)}
                    </span>
                  );
                })()}
                <span className="collapse-icon">
                  {isMeetingsCollapsed ? '▼' : '▲'}
                </span>
              </div>
            </div>
            
            {!isMeetingsCollapsed && (
              <>
                {tasks.meetings.length === 0 ? (
                  <div className="empty-state">
                    <p>No meetings or tasks scheduled for today</p>
                  </div>
                ) : (
                  <GoogleCalendarView 
                    events={tasks.meetings} 
                    followUps={calendarFollowUps}
                    onFollowUpClick={(person) => {
                      console.log('[ToDo] Calendar followUp clicked, opening person:', person.name);
                      setSelectedPerson(person);
                    }}
                    emailPeopleMap={emailPeopleMap}
                    onAddPerson={(attendeeData) => setAddPersonEmail({ 
                      email: attendeeData.email, 
                      name: attendeeData.name || '', 
                      isSent: false 
                    })}
                    onViewPerson={(person) => {
                      console.log('[ToDo] Calendar attendee view clicked, opening person:', person.name);
                      setSelectedPerson(person);
                    }}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* Person Details Panel */}
      {selectedPerson && (
        <PeopleDetails
          data={selectedPerson}
          columns={peopleColumns}
          onClose={() => {
            console.log('[ToDo] PeopleDetails onClose called, closing for person:', selectedPerson?.name);
            setSelectedPerson(null);
            console.log('[ToDo] selectedPerson set to null');
          }}
          onSave={handlePersonSave}
          showToast={showToast}
        />
      )}

      {/* Interaction Context Menu */}
      {interactionMenuPerson && (
        <ContextMenuPortal>
          <InteractionContextMenu
            x={interactionMenuPosition.x}
            y={interactionMenuPosition.y}
            rowData={interactionMenuPerson}
            statusOptions={[]}
            outcomeOptions={["Successful", "Unsuccessful", "Pending"]}
            nextStepOptions={["No Action", "Follow-up Call", "Send Email", "Schedule Meeting"]}
            showToast={showToast}
            onSubmit={handleInteractionSubmit}
            onClose={() => setInteractionMenuPerson(null)}
          />
        </ContextMenuPortal>
      )}

      {/* Email Reply Modal */}
      {replyEmail && (
        <EmailReplyModal
          email={replyEmail}
          replyAll={replyAllMode}
          person={(() => {
            // Determine if this is a sent or received email and get the associated person
            const isSent = replyEmail.labelIds?.includes("SENT");
            return getEmailPerson(replyEmail, isSent);
          })()}
          currentUser={currentUser}
          onClose={() => {
            setReplyEmail(null);
            setReplyAllMode(false);
          }}
          onSend={handleSendEmail}
          showToast={showToast}
        />
      )}

      {/* Email Move Modal */}
      {moveEmail && (
        <EmailMoveModal
          email={moveEmail}
          labels={gmailLabels}
          onClose={() => setMoveEmail(null)}
          onMove={handleMoveEmailToLabel}
          showToast={showToast}
        />
      )}

      {/* Add Person from Email Modal */}
      {addPersonEmail && (
        <AddPersonFromEmailModal
          email={addPersonEmail}
          onClose={() => setAddPersonEmail(null)}
          onAdd={handleAddPerson}
          showToast={showToast}
          users={users}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ToDo;

